import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Shield, UserPlus, AlertCircle, Loader2, CheckCircle2 } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';

const SignupPage = () => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();

  const productKey = params.get('product') || '';
  const tierKey = params.get('tier') || '';
  const billing = params.get('billing') || '';
  const plan = params.get('plan') || '';
  const bundle = params.get('bundle') || '';

  const hasIntent = productKey || tierKey || bundle;

  useEffect(() => { document.title = 'Create your account — DrkLynX'; }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (password.length < 6) { setError('Password must be at least 6 characters'); return; }
    setLoading(true);
    const result = await register(name, email, password);
    setLoading(false);
    if (result.success) {
      const next = hasIntent
        ? `/portal?welcome=1&${params.toString()}`
        : '/portal?welcome=1';
      navigate(next);
    } else {
      setError(result.error);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center px-6 py-12">
      <Helmet>
        <title>Create your account — DrkLynX</title>
        <meta name="description" content="Create your DrkLynX sovereign account. Phase A attestation live across every record; Phase B target ML-DSA-65 in flight, schema-invariant." />
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <div className="w-full max-w-sm">
        <Link to="/" className="flex items-center justify-center gap-2 mb-12">
          <Shield className="w-8 h-8 text-[#00FFFF]" />
          <span className="font-['Unbounded'] font-bold text-2xl tracking-tight text-white">
            Drk<span className="text-[#00FF41]">LynX</span>
          </span>
        </Link>

        <div className="border border-white/10 bg-[#0A0A0A] p-8">
          <div className="flex items-center gap-2 mb-6">
            <UserPlus className="w-4 h-4 text-[#00FFFF]" />
            <span className="font-['JetBrains_Mono'] text-xs tracking-[0.2em] uppercase text-[#00FFFF]">
              // Create Account
            </span>
          </div>

          <h1 data-testid="signup-heading" className="font-['Unbounded'] font-bold text-xl tracking-tighter mb-2 text-white">
            Join DrkLynX
          </h1>
          <p className="font-['Outfit'] font-light text-sm text-[#737373] mb-6">
            Create your quantum-hardened account.
          </p>

          {hasIntent && (
            <div className="mb-6 border border-[#00FFFF]/20 bg-[#00FFFF]/5 px-4 py-3 flex items-start gap-2">
              <CheckCircle2 className="w-4 h-4 text-[#00FFFF] shrink-0 mt-0.5" />
              <div className="font-['Outfit'] text-xs text-[#A3A3A3]">
                <span className="text-white">Continuing your selection</span>
                {plan === 'free' && <> — <span className="text-[#00FF41]">Free tier</span></>}
                {productKey && <> — <span className="text-white">{productKey}</span></>}
                {tierKey && <> · <span className="text-white">{tierKey}</span></>}
                {bundle && <> — <span className="text-white">Sovereign Suite bundle</span></>}
                {billing && <> · <span className="text-[#737373]">{billing}</span></>}
                <div className="text-[#737373] mt-1">We'll route you to checkout right after your account is created.</div>
              </div>
            </div>
          )}

          {error && (
            <div data-testid="signup-error" className="flex items-center gap-2 border border-[#FF3B30]/30 bg-[#FF3B30]/5 px-4 py-3 mb-6">
              <AlertCircle className="w-4 h-4 text-[#FF3B30] shrink-0" />
              <span className="font-['Outfit'] text-sm text-[#FF3B30]">{error}</span>
            </div>
          )}

          <form data-testid="signup-form" onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="signup-name" className="block font-['JetBrains_Mono'] text-[10px] tracking-[0.2em] uppercase text-[#737373] mb-2">Name</label>
              <input
                id="signup-name"
                data-testid="signup-name-input"
                type="text"
                required
                autoComplete="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Your name"
                className="w-full bg-transparent border border-white/10 px-4 py-3 text-sm text-white placeholder:text-[#737373] font-['Outfit'] focus:border-[#00FFFF] outline-none"
              />
            </div>
            <div>
              <label htmlFor="signup-email" className="block font-['JetBrains_Mono'] text-[10px] tracking-[0.2em] uppercase text-[#737373] mb-2">Email</label>
              <input
                id="signup-email"
                data-testid="signup-email-input"
                type="email"
                required
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                className="w-full bg-transparent border border-white/10 px-4 py-3 text-sm text-white placeholder:text-[#737373] font-['Outfit'] focus:border-[#00FFFF] outline-none"
              />
            </div>
            <div>
              <label htmlFor="signup-password" className="block font-['JetBrains_Mono'] text-[10px] tracking-[0.2em] uppercase text-[#737373] mb-2">Password</label>
              <input
                id="signup-password"
                data-testid="signup-password-input"
                type="password"
                required
                autoComplete="new-password"
                minLength={6}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Min 6 characters"
                className="w-full bg-transparent border border-white/10 px-4 py-3 text-sm text-white placeholder:text-[#737373] font-['Outfit'] focus:border-[#00FFFF] outline-none"
              />
              <p className="font-['Outfit'] text-[11px] text-[#737373] mt-2">
                Password hashed with PBKDF2-SHA256 (600k iterations) per OWASP. Phase B target: Argon2id.
              </p>
            </div>
            <button
              data-testid="signup-submit-button"
              type="submit"
              disabled={loading}
              className="w-full flex items-center justify-center gap-2 border border-[#00FFFF] text-[#00FFFF] py-3 text-xs font-['JetBrains_Mono'] tracking-[0.15em] uppercase transition-colors duration-300 disabled:opacity-50 mt-6 hover:bg-[#00FFFF] hover:text-black"
            >
              {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Creating account...</> : <>Create Account</>}
            </button>
          </form>

          <div className="mt-6 text-center space-y-3">
            <Link
              data-testid="auth-mode-toggle"
              to="/login"
              className="block font-['Outfit'] text-sm text-[#A3A3A3] hover:text-white transition-colors duration-300"
            >
              Already have an account? <span className="font-medium text-[#00FF41]">Sign in</span>
            </Link>
          </div>

          <p className="font-['Outfit'] text-[10px] text-[#737373] text-center mt-6 leading-relaxed">
            By creating an account you agree to the{' '}
            <Link to="/legal/terms" className="underline hover:text-white">Terms of Service</Link>
            {' '}and{' '}
            <Link to="/legal/privacy" className="underline hover:text-white">Privacy Policy</Link>.
            Phase A attestation live (HMAC-SHA256); Phase B target ML-DSA-65 / NIST FIPS 204 in flight.
          </p>
        </div>

        <div className="text-center mt-6">
          <Link to="/" className="font-['JetBrains_Mono'] text-[10px] tracking-[0.15em] uppercase text-[#737373] hover:text-[#00FF41] transition-colors duration-300">
            Back to DrkLynX.com
          </Link>
        </div>
      </div>
    </div>
  );
};

export default SignupPage;
